import { useState } from 'react';
import type { ChatParticipant, ChatSession, ExperienceMode, MediaActionApprovalRequest } from '@localagent/sdk';
import { MediaActionApprovalCard } from './MediaActionApprovalCard';

interface SubagentMediaApprovalProps {
  chat: ChatSession;
  participantId: string;
  label: string;
  request: MediaActionApprovalRequest;
  mode?: ExperienceMode;
}

function SubagentMediaApproval({ chat, participantId, label, request, mode }: SubagentMediaApprovalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const answer = (approved: boolean) => {
    setBusy(true);
    setError(null);
    try {
      chat.respondToMediaActionApproval({ approvalId: request.approvalId, approved });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MediaActionApprovalCard
      request={request}
      onApprove={() => answer(true)}
      onDeny={() => answer(false)}
      isSubmitting={busy}
      error={error}
      mode={mode}
      askedBy={{ participantId, label }}
    />
  );
}

export interface SubagentMediaApprovalsProps {
  chat: ChatSession;
  participants: readonly ChatParticipant[];
  /** Only this participant's request; every subagent's when absent. */
  participantId?: string;
  mode?: ExperienceMode;
}

// (agents ADR-055)
export function SubagentMediaApprovals({ chat, participants, participantId, mode }: SubagentMediaApprovalsProps) {
  const pending = chat.participantMediaActionApprovals.filter(
    (a) => participantId === undefined || a.participantId === participantId,
  );
  if (pending.length === 0) return null;
  return (
    <div className="mx-auto w-full max-w-2xl space-y-2 px-4 pb-2" data-testid="subagent-media-approvals">
      {pending.map(({ participantId: id, request }) => {
        const participant = participants.find((p) => p.id === id);
        if (!participant) throw new Error(`media action approval ${request.approvalId} names unknown participant ${id}`);
        return (
          <SubagentMediaApproval
            key={request.approvalId}
            chat={chat}
            participantId={id}
            label={participant.label}
            request={request}
            mode={mode}
          />
        );
      })}
    </div>
  );
}
